import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { CreateUserPayload, UpdateUserPayload, User } from '../types';
import { useCreateUserMutation, useUpdateUserMutation, useLazyCheckEmailQuery } from '../api/usersApiSlice';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectIsSuperAdmin } from '../../../features/auth/authSlice';

interface UserFormProps {
  mode: 'create' | 'edit';
  user?: User | null;
  empresas: Array<{ id: number; nombre: string }>;
  onClose: () => void;
}

export const UserForm = ({ mode, user, empresas, onClose }: UserFormProps) => {
  // Obtener usuario actual y verificar si es superadmin
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  
  // Estado para controlar la visibilidad de los permisos
  const [showPermissions, setShowPermissions] = useState(false);
  
  // Estado para controlar el rol seleccionado
  const [selectedRole, setSelectedRole] = useState<string>('user');
  
  // Inicializar formulario con react-hook-form
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CreateUserPayload>({
    defaultValues: mode === 'edit' && user
      ? {
          email: user.email,
          role: user.role,
          empresaId: user.empresa_id,
          // No incluimos password en edición
          password: '',
          // Permisos
          bot1_enabled: user.bot1_enabled,
          bot2_enabled: user.bot2_enabled,
          bot3_enabled: user.bot3_enabled,
          bot4_enabled: user.bot4_enabled,
          bot5_enabled: user.bot5_enabled,
          bot6_enabled: user.bot6_enabled,
          bot7_enabled: user.bot7_enabled,
          bot8_enabled: user.bot8_enabled,
          bot9_enabled: user.bot9_enabled,
          bot10_enabled: user.bot10_enabled,
          clientes_enabled: user.clientes_enabled,
        }
      : {
          email: '',
          role: 'user',
          empresaId: isSuperAdmin ? undefined : currentUser?.empresa_id,
          password: '',
          // Permisos default (false para users)
          bot1_enabled: false,
          bot2_enabled: false,
          bot3_enabled: false,
          bot4_enabled: false,
          bot5_enabled: false,
          bot6_enabled: false,
          bot7_enabled: false,
          bot8_enabled: false,
          bot9_enabled: false,
          bot10_enabled: false,
          clientes_enabled: false,
        }
  });
  
  // Hooks de RTK Query para mutaciones
  const [createUser, { isLoading: isCreating, error: createError }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating, error: updateError }] = useUpdateUserMutation();
  const [checkEmailTrigger] = useLazyCheckEmailQuery();
  
  // Estado para controlar errores personalizados
  const [customError, setCustomError] = useState<string | null>(null);
  
  // Determinar estado de carga y error
  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;
  
  // Observar cambios en el rol seleccionado
  const watchRole = watch('role');
  
  // Efecto para manejar cambios en el rol
  useEffect(() => {
    // Si el rol cambia a admin o superadmin, activar todos los permisos
    if (watchRole === 'admin' || watchRole === 'superadmin') {
      const updatedPermissions = {
        bot1_enabled: true,
        bot2_enabled: true,
        bot3_enabled: true,
        bot4_enabled: true,
        bot5_enabled: true,
        bot6_enabled: true,
        bot7_enabled: true,
        bot8_enabled: true,
        bot9_enabled: true,
        bot10_enabled: true,
        clientes_enabled: true
      };
      
      // Actualizar valores del formulario
      Object.keys(updatedPermissions).forEach(key => {
        setValue(key as keyof CreateUserPayload, true);
      });
      
      // Mostrar sección de permisos si no estaba visible
      setShowPermissions(true);
    }
    
    // Actualizar estado de rol seleccionado
    setSelectedRole(watchRole);
  }, [watchRole, setValue]);
  
  // Efecto para asegurarnos que los valores son correctos cuando cambian las props
  useEffect(() => {
    if (mode === 'edit' && user) {
      setValue('email', user.email);
      setValue('role', user.role);
      setValue('empresaId', user.empresa_id);
      
      // Cargar permisos del usuario existente o aplicar defaults según rol
      const isAdminOrSuperadmin = user.role === 'admin' || user.role === 'superadmin';
      
      // Configurar permisos según valores existentes o rol
      const permissions = {
        bot1_enabled: user.bot1_enabled ?? isAdminOrSuperadmin,
        bot2_enabled: user.bot2_enabled ?? isAdminOrSuperadmin,
        bot3_enabled: user.bot3_enabled ?? isAdminOrSuperadmin,
        bot4_enabled: user.bot4_enabled ?? isAdminOrSuperadmin,
        bot5_enabled: user.bot5_enabled ?? isAdminOrSuperadmin,
        bot6_enabled: user.bot6_enabled ?? isAdminOrSuperadmin,
        bot7_enabled: user.bot7_enabled ?? isAdminOrSuperadmin,
        bot8_enabled: user.bot8_enabled ?? isAdminOrSuperadmin,
        bot9_enabled: user.bot9_enabled ?? isAdminOrSuperadmin,
        bot10_enabled: user.bot10_enabled ?? isAdminOrSuperadmin,
        clientes_enabled: user.clientes_enabled ?? isAdminOrSuperadmin,
      };
      
      // Actualizar valores del formulario
      Object.keys(permissions).forEach(key => {
        setValue(key as keyof CreateUserPayload, permissions[key as keyof typeof permissions]);
      });
      
      setSelectedRole(user.role);
      
      console.log('Formulario actualizado con datos de usuario:', { 
        email: user.email, 
        role: user.role, 
        empresaId: user.empresa_id,
        permissions
      });
    }
  }, [user, mode, setValue]);
  
  // Enviar el formulario
  const onSubmit: SubmitHandler<CreateUserPayload> = async (data) => {
    // Limpiar errores anteriores
    setCustomError(null);
    
    try {
      // Si no es superadmin, forzar la empresa del usuario actual
      if (!isSuperAdmin) {
        data.empresaId = currentUser?.empresa_id;
        // Si no es superadmin, solo puede crear/editar usuarios
        data.role = 'user';
      }
      // Si el usuario a editar es superadmin, no permitir edición
      if (mode === 'edit' && user?.role === 'superadmin') {
        throw new Error('No está permitido editar usuarios con rol superadmin');
      }
      // Si el usuario es admin y está editando un admin, no permitir cambio de rol
      if (!isSuperAdmin && mode === 'edit' && user?.role === 'admin') {
        data.role = 'admin';
      }
      // Aplicar reglas de permisos según el rol
      if (data.role === 'admin') {
        data.bot1_enabled = true;
        data.bot2_enabled = true;
        data.bot3_enabled = true;
        data.bot4_enabled = true;
        data.bot5_enabled = true;
        data.bot6_enabled = true;
        data.bot7_enabled = true;
        data.bot8_enabled = true;
        data.bot9_enabled = true;
        data.bot10_enabled = true;
        data.clientes_enabled = true;
      }
      console.log('Datos del formulario a enviar:', data);

      // Asegurar que empresaId sea un número antes de enviar la solicitud
      if (data.empresaId) {
        data.empresaId = Number(data.empresaId);
      }
      
      // Asegurar que todos los campos booleanos estén explícitamente definidos
      const booleanFields = [
        'bot1_enabled', 'bot2_enabled', 'bot3_enabled', 'bot4_enabled', 'bot5_enabled',
        'bot6_enabled', 'bot7_enabled', 'bot8_enabled', 'bot9_enabled', 'bot10_enabled',
        'clientes_enabled'
      ] as Array<keyof Pick<CreateUserPayload, 
        'bot1_enabled' | 'bot2_enabled' | 'bot3_enabled' | 'bot4_enabled' | 'bot5_enabled' |
        'bot6_enabled' | 'bot7_enabled' | 'bot8_enabled' | 'bot9_enabled' | 'bot10_enabled' |
        'clientes_enabled'
      >>;
      
      booleanFields.forEach(field => {
        if (data[field] === undefined) {
          data[field] = false;
        }
      });
      
      // Si estamos creando un nuevo usuario, verificar si el email ya existe
      if (mode === 'create') {
        try {
          // Verificar si el email ya existe usando el hook de RTK Query
          const { data: emailCheckResult } = await checkEmailTrigger(data.email);
          
          if (emailCheckResult?.exists) {
            setCustomError('Este email ya está registrado. Por favor, utiliza otro.');
            return;
          }
        } catch (error) {
          console.error('Error al verificar email:', error);
          // Si hay un error en la verificación, intentamos crear de todos modos
        }
      }
      
      try {
        if (mode === 'create') {
          const result = await createUser(data).unwrap();
          console.log('Usuario creado exitosamente:', result);
          onClose();
        } else if (mode === 'edit' && user) {
          const changes: UpdateUserPayload = { ...data };
          if (!changes.password) {
            delete changes.password;
          }
          // La conversión ya se hizo arriba, no necesitamos repetirla
          const result = await updateUser({
            id: user.id,
            changes
          }).unwrap();
          console.log('Usuario actualizado exitosamente:', result);
          onClose();
        }
      } catch (apiError: unknown) {
        console.error('Error detallado de la API:', apiError);
        // Manejar específicamente el error de email duplicado
        if ((apiError as {data?: {message?: string, details?: string}})?.data?.details === "Validation error") {
          setCustomError('Este email ya está registrado. Por favor, utiliza otro.');
        }
      }
    } catch (err) {
      console.error('Error al guardar usuario:', err);
    }
  };
  
  if (!empresas || empresas.length === 0) {
    return (
      <div className="fixed inset-0 bg-foreground/20 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-card text-card-foreground p-6 rounded-lg shadow-lg w-full max-w-md relative border border-border flex flex-col items-center justify-center">
          <span className="text-muted-foreground text-lg">Cargando empresas...</span>
          <div className="mt-4 animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-foreground/20 backdrop-blur-sm flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-card text-card-foreground p-6 rounded-lg shadow-lg w-full max-w-md relative border border-border max-h-[90vh] overflow-y-auto">
        {/* Botón de cerrar en la esquina */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          aria-label="Cerrar"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
        
        <h2 className="text-xl font-bold mb-6 text-foreground">
          {mode === 'create' ? 'Crear Nuevo Usuario' : 'Editar Usuario'}
        </h2>
        
        {/* Mostrar error si existe */}
        {(error || customError) && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive px-4 py-3 rounded-md mb-4">
            <p className="text-sm">
              {customError || 
                ((error as {data?: {message?: string, details?: string}})?.data?.details === "Validation error" 
                ? "El email ya existe en la base de datos. Por favor, utiliza otro email."
                : (error as {data?: {message?: string}})?.data?.message || 'Ha ocurrido un error al procesar la solicitud.')}
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Campo Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              {...register("email", { 
                required: "El email es obligatorio",
                pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Email inválido" }
              })}
              className={`block w-full px-3 py-2 border ${errors.email ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground sm:text-sm`}
              disabled={isLoading}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>
          
          {/* Campo Password - Obligatorio en creación, opcional en edición */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
              {mode === 'create' ? 'Contraseña' : 'Nueva Contraseña (dejar en blanco para no cambiar)'}
            </label>
            <input
              id="password"
              type="password"
              {...register("password", { 
                required: mode === 'create' ? "La contraseña es obligatoria" : false,
                minLength: { value: 6, message: "La contraseña debe tener al menos 6 caracteres" }
              })}
              className={`block w-full px-3 py-2 border ${errors.password ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground sm:text-sm`}
              disabled={isLoading}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>
          
          {/* Campo Rol - Solo superadmin puede asignar rol admin, admin solo puede asignar user */}
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-foreground mb-1">
              Rol
            </label>
            {((mode === 'edit' && (user?.role === 'admin' || user?.role === 'superadmin')) || (!isSuperAdmin && mode === 'edit')) ? (
              <input
                type="text"
                value={watch('role') === 'admin' ? 'Administrador' : 'Usuario'}
                disabled
                className="block w-full px-3 py-2 border border-border rounded-md shadow-sm bg-background text-foreground sm:text-sm"
              />
            ) : (
              <select
                id="role"
                {...register("role", { required: "El rol es obligatorio" })}
                className={`block w-full px-3 py-2 border ${errors.role ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground sm:text-sm`}
                disabled={isLoading}
              >
                <option value="user">Usuario</option>
                {isSuperAdmin && (mode === 'create' || (mode === 'edit' && user?.role === 'admin')) && <option value="admin">Administrador</option>}
              </select>
            )}
            {errors.role && (
              <p className="mt-1 text-sm text-destructive">{errors.role.message}</p>
            )}
            {!isSuperAdmin && (
              <p className="mt-1 text-xs text-muted-foreground italic">Solo puedes crear usuarios con rol de usuario normal</p>
            )}
            {mode === 'edit' && user?.role === 'superadmin' && (
              <p className="mt-1 text-xs text-destructive italic">No está permitido editar usuarios con rol superadmin.</p>
            )}
            {mode === 'edit' && user?.role === 'admin' && !isSuperAdmin && (
              <p className="mt-1 text-xs text-destructive italic">No está permitido cambiar el rol de un administrador.</p>
            )}
          </div>
          
          {/* Campo Empresa - Solo visible para superadmin */}
          <div>
            <label htmlFor="empresaId" className="block text-sm font-medium text-foreground mb-1">
              Empresa
            </label>
            <select
              id="empresaId"
              {...register("empresaId", { 
                required: "La empresa es obligatoria"
              })}
              className={`block w-full px-3 py-2 border ${errors.empresaId ? 'border-destructive' : 'border-border'} rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary bg-background text-foreground sm:text-sm`}
              disabled={isLoading || !isSuperAdmin}
            >
              <option value="">-- Seleccione una empresa --</option>
              {empresas.map(empresa => (
                <option key={empresa.id} value={empresa.id}>{empresa.nombre}</option>
              ))}
            </select>
            {errors.empresaId && (
              <p className="mt-1 text-sm text-destructive">{errors.empresaId.message}</p>
            )}
            {!isSuperAdmin && currentUser?.empresa_id && (
              <p className="mt-1 text-xs text-muted-foreground italic">
                Los usuarios se crearán automáticamente en tu empresa
              </p>
            )}
          </div>
          
          {/* Permisos - Mostrar/ocultar sección */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setShowPermissions(!showPermissions)}
              className="text-sm font-medium text-primary hover:text-primary/80 flex items-center"
            >
              {showPermissions ? '▼ Ocultar permisos' : '► Mostrar permisos'}
            </button>
          </div>
          
          {/* Sección de permisos */}
          {showPermissions && (
            <div className="space-y-3 border border-border rounded-md p-4 bg-muted/20">
              <h3 className="font-medium text-foreground text-sm mb-2">Permisos de acceso</h3>
              
              {/* Permisos para clientes */}
              <div>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="clientes_enabled"
                    {...register("clientes_enabled")}
                    className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    disabled={isLoading || selectedRole === 'superadmin'}
                  />
                  <label htmlFor="clientes_enabled" className="ml-2 block text-sm text-foreground">
                    Acceso a gestión de clientes
                  </label>
                </div>
              </div>
              
              {/* Título de la sección de bots */}
              <h4 className="font-medium text-muted-foreground text-xs mt-3 mb-2">Acceso a bots</h4>
              
              {/* Grid para checkboxes de bots */}
              <div className="grid grid-cols-2 gap-2">
                {Array.from({length: 10}, (_, i) => i + 1).map((num) => (
                  <div key={num} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`bot${num}_enabled`}
                      {...register(`bot${num}_enabled` as keyof CreateUserPayload)}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      disabled={isLoading || selectedRole === 'superadmin'}
                    />
                    <label htmlFor={`bot${num}_enabled`} className="ml-2 block text-sm text-foreground">
                      Bot {num}
                    </label>
                  </div>
                ))}
              </div>
              
              {selectedRole === 'admin' && (
                <p className="text-xs text-primary italic mt-2">
                  Los administradores tienen todos los permisos activados por defecto, pero pueden editarse.
                </p>
              )}
              
              {selectedRole === 'superadmin' && (
                <p className="text-xs text-primary italic mt-2">
                  Los superadmins siempre tienen todos los permisos activados. No pueden desactivarse.
                </p>
              )}
            </div>
          )}
          
          {/* Botones de acción */}
          <div className="pt-4 flex justify-end space-x-3 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-muted text-foreground rounded-md hover:bg-accent"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading 
                ? (mode === 'create' ? 'Creando...' : 'Guardando...') 
                : (mode === 'create' ? 'Crear Usuario' : 'Guardar Cambios')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 